import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { ownerName, isPastDue, isClosedWithRunning, deadlineUrgency, termLabel } from '../utils/labFilters';
import { countLabel, EMPTY_ENVIRONMENT_STATS } from '../utils/environmentStats';
import { AdditionalEnvironmentStates } from './EnvironmentStatsStatus';

/**
 * Compact list and table renderings of the Labs list.
 *
 * Cards are right for a dozen labs and wrong for two hundred: at ~230px tall
 * each, twelve already fill the viewport. These two denser views trade the
 * visual weight for scanability once a term's worth of labs exists.
 */

const toneColor = {
  critical: 'var(--accent-danger)',
  warning: 'var(--accent-warning)',
  success: 'var(--accent-success)',
  muted: 'var(--text-muted)',
};

function statusTone(status) {
  if (status === 'open') return 'success';
  if (status === 'closed') return 'critical';
  return 'warning';
}

function StatusTag({ status }) {
  const tone = statusTone(status);
  const bg = {
    success: 'var(--badge-success-bg)',
    critical: 'var(--badge-danger-bg)',
    warning: 'var(--badge-warning-bg)',
  }[tone];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      fontFamily: 'var(--font-mono)', background: bg, color: toneColor[tone],
    }}>
      {status}
    </span>
  );
}

/** Why this lab is flagged, in plain language — or null when it is healthy. */
function attentionReason(lab, now) {
  if (isPastDue(lab, now)) return 'Past due and still open';
  if (isClosedWithRunning(lab)) {
    const n = lab.containerStats?.running || 0;
    return `Closed, but ${n} container${n === 1 ? '' : 's'} still running`;
  }
  return null;
}

const num = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };

function LabRow({ lab, now, selected, onSelect, compact }) {
  const stats = lab.containerStats || EMPTY_ENVIRONMENT_STATS;
  const reason = attentionReason(lab, now);
  const urgency = deadlineUrgency(lab, now);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: onSelect
          ? '28px minmax(200px, 2.2fr) 92px minmax(120px, 1fr) 110px 130px 100px 36px'
          : 'minmax(200px, 2.2fr) 92px minmax(120px, 1fr) 110px 130px 100px 36px',
        gap: 12, alignItems: 'center',
        padding: compact ? '9px 14px' : '13px 14px',
        background: 'var(--bg-secondary)',
        boxShadow: reason ? 'inset 3px 0 0 var(--accent-danger)' : undefined,
        fontSize: 13.5,
      }}
    >
      {onSelect && (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(lab.id)}
          aria-label={`Select ${lab.name}`}
          style={{ width: 15, height: 15, cursor: 'pointer' }}
        />
      )}

      <div style={{ minWidth: 0 }}>
        <Link
          to={`/labs/${lab.id}`}
          style={{
            ...num, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)',
            textDecoration: 'none', display: 'block', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {lab.name}
        </Link>
        <span style={{ ...num, fontSize: 11, color: 'var(--text-muted)' }}>
          {lab.courseCode}{lab.section ? `-S${lab.section}` : ''}
          {lab.isSemesterLab ? ' · semester' : ''}
        </span>
      </div>

      <div><StatusTag status={lab.status} /></div>

      <div style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ownerName(lab)}
      </div>

      <div style={{ ...num, fontSize: 12, color: 'var(--text-secondary)' }}>
        {lab.studentCount || 0} student{(lab.studentCount || 0) === 1 ? '' : 's'}
      </div>

      <div style={{ ...num, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{ color: stats.running ? 'var(--accent-success)' : 'var(--text-muted)' }}>{countLabel(stats.running)}</span>
        {' running env / '}
        {countLabel(stats.stopped)} stopped
        <div><AdditionalEnvironmentStates stats={stats} /></div>
      </div>

      <div style={{ fontSize: 11.5, color: urgency ? toneColor[urgency.tone] : 'var(--text-muted)' }}>
        {urgency ? urgency.label : 'No deadline'}
      </div>

      <Link to={`/labs/${lab.id}`} aria-label={`Open ${lab.name}`} style={{ color: 'var(--text-muted)', display: 'flex' }}>
        <ExternalLink size={14} />
      </Link>

      {reason && (
        <div style={{
          gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11.5, color: 'var(--accent-danger)', marginTop: -2,
        }}>
          <AlertCircle size={12} aria-hidden="true" />
          {reason}
        </div>
      )}
    </div>
  );
}

export default function LabListView({
  groups, now, view,
  selectedIds, onSelect, onSelectGroup,
  collapsedGroups, onToggleGroup,
}) {
  const compact = view === 'table';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {groups.map((group) => {
        const isCollapsed = collapsedGroups.includes(group.key);
        const groupIds = group.labs.map((l) => l.id);
        const allSelected = onSelect && groupIds.length > 0 && groupIds.every((id) => selectedIds.includes(id));

        return (
          <div key={group.key}>
            {group.label && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                paddingBottom: 6, borderBottom: '1px solid var(--border-primary)',
              }}>
                <button
                  type="button"
                  onClick={() => onToggleGroup(group.key)}
                  aria-expanded={!isCollapsed}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: 'var(--text-primary)', fontSize: 14, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                  }}
                >
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{isCollapsed ? '▶' : '▼'}</span>
                  {group.label}
                </button>
                <span style={{
                  ...num, fontSize: 11, color: 'var(--text-muted)',
                  background: 'var(--bg-tertiary)', borderRadius: 999, padding: '1px 7px',
                }}>
                  {group.labs.length}
                </span>
                {onSelect && !isCollapsed && (
                  <button
                    type="button"
                    onClick={() => onSelectGroup(groupIds, !allSelected)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--accent-primary)', fontSize: 11.5, fontFamily: 'inherit', padding: 0,
                    }}
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
            )}

            {!isCollapsed && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 1,
                background: 'var(--border-primary)', border: '1px solid var(--border-primary)',
                borderRadius: 10, overflow: 'hidden',
              }}>
                {group.labs.map((lab) => (
                  <LabRow
                    key={lab.id}
                    lab={lab}
                    now={now}
                    compact={compact}
                    selected={selectedIds.includes(lab.id)}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
